import { Controller } from "@hotwired/stimulus"
import { post, patch, destroy } from "@rails/request.js"
import { escapeHtml } from "../lib/html_sanitizer"
import { showConfirmModal } from "../lib/custom_confirm"
import { syncGateReason } from "../lib/gate_reason"
import { SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"
import { ROW_ACTION_CLASS, ROW_ACTION_DANGER_CLASS, ROW_ACTIONS_STRIP_CLASS } from "../lib/row_action"

// The reason the Add Tag submit gives while a blank name disables it (A11Y-1 /
// BUTTON-4). The view stamps the same text as data-tags-manager-disabled-reason
// so the controller re-applies exactly what was painted; this is the fallback
// for a host that carries no stamp (keep the two in step).
export const DEFAULT_DISABLED_REASON = "Type a tag name"
// While the create request is in flight the submit is disabled for a different
// reason, and says so rather than going silent.
export const BUSY_REASON = "Adding the tag…"
// #13869 (owner ruling 2026-09-22): a rename onto a name the agent already
// owns is saved under a numbered counter instead of erroring; the server
// answers `suffixed: true` with `requested_name`, and this is what we tell
// them. The row is left in its normal state so they can rename it again.
export const suffixedRenameNotice = ({ requested_name: requestedName, name }) =>
  `You already have a tag named "${requestedName}", so this one was saved as "${name}". Rename it again to choose a different name.`

export default class extends Controller {
  static targets = [
    "nameInput",
    "submitButton",
    "tagsList",
    "customTagsList",
    "emptyState",
    "customEmptyState",
    "customCount",
    "badge",
    "editInput",
    "saveButton",
    "cancelButton"
  ]

  connect() {
    this.#updateSubmitState()
  }

  // BUTTON-9: a row's rename Save follows its input — every edit input (server
  // rendered or appended after a create) re-checks the gate as it is typed in.
  editInputTargetConnected(input) {
    input.addEventListener("input", this.#onEditInput)
  }

  editInputTargetDisconnected(input) {
    input.removeEventListener("input", this.#onEditInput)
  }

  // Enable the "Add Tag" button only once the input has content (dirty).
  nameInputChanged() {
    this.#updateSubmitState()
  }

  async create(event) {
    event.preventDefault()

    const name = this.nameInputTarget.value.trim()
    if (!name) return

    syncGateReason(this.submitButtonTarget, { ready: false, reason: BUSY_REASON })

    try {
      const response = await post("/tags.json", {
        body: JSON.stringify({ tag: { name } }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.#appendTag(data.id, data.name)
        this.nameInputTarget.value = ""
        this.#hideEmptyState()
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to create tag", "danger")
      }
    } catch {
      window.showToast("Failed to create tag", "danger")
    } finally {
      this.nameInputTarget.focus()
      this.#updateSubmitState()
    }
  }

  startEdit(event) {
    const item = event.currentTarget.closest(".tags-list-item")
    const badge = item.querySelector("[data-tags-manager-target='badge']")
    const input = item.querySelector("[data-tags-manager-target='editInput']")
    const editBtn = event.currentTarget
    const saveBtn = item.querySelector("[data-tags-manager-target='saveButton']")
    const cancelBtn = item.querySelector("[data-tags-manager-target='cancelButton']")
    const deleteBtn = item.querySelector("[data-action='click->tags-manager#delete']")

    badge.classList.add("d-none")
    input.classList.remove("d-none")
    input.value = badge.textContent
    input.focus()
    input.select()

    editBtn.classList.add("d-none")
    deleteBtn.classList.add("d-none")
    saveBtn.classList.remove("d-none")
    cancelBtn.classList.remove("d-none")
    this.#syncRenameSave(item)
  }

  async saveEdit(event) {
    const saveBtn = event.currentTarget
    const item = saveBtn.closest(".tags-list-item")
    const tagId = item.dataset.tagId
    const input = item.querySelector("[data-tags-manager-target='editInput']")
    const newName = input.value.trim()

    if (!newName) return

    saveBtn.disabled = true
    saveBtn.setAttribute("aria-busy", "true")
    try {
      const response = await patch(`/tags/${tagId}.json`, {
        body: JSON.stringify({ tag: { name: newName } }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        const badge = item.querySelector("[data-tags-manager-target='badge']")
        badge.textContent = data.name
        item.dataset.tagId = data.id
        this.#updateActionLabels(item, data.name)
        this.#exitEdit(item)
        if (data.suffixed) window.showToast(suffixedRenameNotice(data), "info")
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to rename tag", "danger")
      }
    } catch {
      window.showToast("Failed to rename tag", "danger")
    } finally {
      saveBtn.removeAttribute("aria-busy")
      // A landed rename closed the row: its hidden Save goes back to its own
      // enabled state (startEdit re-gates it). A failed one stays open, dirty.
      if (saveBtn.classList.contains("d-none")) {
        syncGateReason(saveBtn, { ready: true, reason: SAVE_UNCHANGED_REASON })
      } else {
        this.#syncRenameSave(item)
      }
    }
  }

  cancelEdit(event) {
    const item = event.currentTarget.closest(".tags-list-item")
    this.#exitEdit(item)
  }

  editKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      const item = event.currentTarget.closest(".tags-list-item")
      const saveBtn = item.querySelector("[data-tags-manager-target='saveButton']")
      this.#syncRenameSave(item)
      saveBtn.click()
    } else if (event.key === "Escape") {
      const item = event.currentTarget.closest(".tags-list-item")
      this.#exitEdit(item)
    }
  }

  async delete(event) {
    const item = event.currentTarget.closest(".tags-list-item")
    const tagId = item.dataset.tagId
    const badge = item.querySelector("[data-tags-manager-target='badge']")
    const tagName = badge.textContent

    const confirmed = await showConfirmModal(`Delete tag "${tagName}"? It will be removed from all your leads and orders.`)
    if (!confirmed) return

    try {
      const response = await destroy(`/tags/${tagId}.json`, {
        responseKind: "json"
      })

      if (response.ok) {
        item.remove()
        this.#showEmptyStateIfNeeded()
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to delete tag", "danger")
      }
    } catch {
      window.showToast("Failed to delete tag", "danger")
    }
  }

  // Private helpers

  #onEditInput = (event) => {
    const item = event.currentTarget.closest(".tags-list-item")
    if (item) this.#syncRenameSave(item)
  }

  // BUTTON-9: the rename Save is enabled only while the input holds a name that
  // differs from the one the row shows (the badge keeps the loaded name until a
  // rename lands), and says why while it is not. A blank name gives the Add
  // field's reason; the in-flight PATCH keeps it disabled until it settles.
  #syncRenameSave(item) {
    const saveBtn = item.querySelector("[data-tags-manager-target='saveButton']")
    const input = item.querySelector("[data-tags-manager-target='editInput']")
    const badge = item.querySelector("[data-tags-manager-target='badge']")
    if (!saveBtn || !input || !badge) return

    const name = input.value.trim()
    if (!name) {
      syncGateReason(saveBtn, { ready: false, reason: DEFAULT_DISABLED_REASON })
    } else {
      syncGateReason(saveBtn, { ready: name !== badge.textContent.trim(), reason: SAVE_UNCHANGED_REASON })
    }
  }

  // A11Y-1 / BUTTON-4: disabled while the name is blank AND explained — the
  // reason it was painted with as its gl-tooltip plus a "<label> — <reason>"
  // name; both withdrawn the moment a name enables it (lib/gate_reason).
  #updateSubmitState() {
    if (!this.hasSubmitButtonTarget || !this.hasNameInputTarget) return
    const ready = this.nameInputTarget.value.trim() !== ""
    syncGateReason(this.submitButtonTarget, { ready, reason: this.#disabledReason() })
  }

  #disabledReason() {
    return this.submitButtonTarget.dataset.tagsManagerDisabledReason || DEFAULT_DISABLED_REASON
  }

  #appendTag(id, name) {
    const html = `
      <div class="tags-list-item" data-tag-id="${id}">
        <div class="gl-tags-row-main">
          <span class="tag-badge" data-tags-manager-target="badge">${escapeHtml(name)}</span>
          <input type="text" class="gl-form-control d-none"
                 value="${escapeHtml(name)}" maxlength="50"
                 data-tags-manager-target="editInput"
                 data-action="keydown->tags-manager#editKeydown">
        </div>
        <div class="gl-tags-row-actions">
          <div class="${ROW_ACTIONS_STRIP_CLASS}" role="group" aria-label="${escapeHtml(name)} actions">
          <button type="button" class="${ROW_ACTION_CLASS}" data-gl-tooltip="Rename" aria-label="Rename ${escapeHtml(name)}"
                  data-action="click->tags-manager#startEdit">
            <i class="ph ph-pencil" aria-hidden="true"></i>
          </button>
          <button type="button" class="${ROW_ACTION_CLASS} d-none" data-gl-tooltip="Save" aria-label="Save ${escapeHtml(name)}"
                  data-action="click->tags-manager#saveEdit"
                  data-tags-manager-target="saveButton">
            <i class="ph ph-check" aria-hidden="true"></i>
          </button>
          <button type="button" class="${ROW_ACTION_CLASS} d-none" data-gl-tooltip="Cancel" aria-label="Cancel renaming ${escapeHtml(name)}"
                  data-action="click->tags-manager#cancelEdit"
                  data-tags-manager-target="cancelButton">
            <i class="ph ph-x" aria-hidden="true"></i>
          </button>
          <button type="button" class="${ROW_ACTION_DANGER_CLASS}" data-gl-tooltip="Delete" aria-label="Delete ${escapeHtml(name)}"
                  data-action="click->tags-manager#delete">
            <i class="ph ph-trash" aria-hidden="true"></i>
          </button>
          </div>
        </div>
      </div>
    `
    this.#customTagsListTarget().insertAdjacentHTML("beforeend", html)
    this.#updateCustomCount()
  }

  #exitEdit(item) {
    const badge = item.querySelector("[data-tags-manager-target='badge']")
    const input = item.querySelector("[data-tags-manager-target='editInput']")
    const editBtn = item.querySelector("[data-action='click->tags-manager#startEdit']")
    const saveBtn = item.querySelector("[data-tags-manager-target='saveButton']")
    const cancelBtn = item.querySelector("[data-tags-manager-target='cancelButton']")
    const deleteBtn = item.querySelector("[data-action='click->tags-manager#delete']")

    badge.classList.remove("d-none")
    input.classList.add("d-none")
    editBtn.classList.remove("d-none")
    deleteBtn.classList.remove("d-none")
    saveBtn.classList.add("d-none")
    cancelBtn.classList.add("d-none")
  }

  #updateActionLabels(item, name) {
    const labels = [
      ["[data-action='click->tags-manager#startEdit']", `Rename ${name}`],
      ["[data-action='click->tags-manager#saveEdit']", `Save ${name}`],
      ["[data-action='click->tags-manager#cancelEdit']", `Cancel renaming ${name}`],
      ["[data-action='click->tags-manager#delete']", `Delete ${name}`],
      [".gl-row-actions", `${name} actions`]
    ]

    labels.forEach(([selector, label]) => {
      const button = item.querySelector(selector)
      if (!button) return
      button.setAttribute("aria-label", label)
      // A gate-held button (the rename Save) restores its own name from here.
      if (button.hasAttribute("data-gl-gate-label")) button.setAttribute("data-gl-gate-label", label)
    })
  }

  #hideEmptyState() {
    const emptyState = this.#customEmptyStateTarget()
    if (emptyState) {
      emptyState.classList.add("d-none")
    }
  }

  #showEmptyStateIfNeeded() {
    this.#updateCustomCount()

    const emptyState = this.#customEmptyStateTarget()
    if (!emptyState) return

    if (this.#customTagCount() === 0) {
      emptyState.classList.remove("d-none")
    } else {
      emptyState.classList.add("d-none")
    }
  }

  #customTagsListTarget() {
    return this.hasCustomTagsListTarget ? this.customTagsListTarget : this.tagsListTarget
  }

  #customEmptyStateTarget() {
    if (this.hasCustomEmptyStateTarget) return this.customEmptyStateTarget
    if (this.hasEmptyStateTarget) return this.emptyStateTarget
    return null
  }

  #customTagCount() {
    return this.#customTagsListTarget().querySelectorAll(".tags-list-item").length
  }

  #updateCustomCount() {
    if (!this.hasCustomCountTarget) return

    const count = this.#customTagCount()
    const noun = count === 1 ? "custom tag" : "custom tags"
    this.customCountTarget.textContent = `${count} ${noun}`
  }
}
